/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useCallback, useMemo } from 'react';
import { Code2 } from 'lucide-react';
import { useDevModeStore } from '../../../stores';
import { useAllProducts } from '../../../stores/marketStore';
import { useDeviceType } from '../../../hooks/useDeviceType';
import { DevModeReference } from '../../devmode/DevModeReference';
import { DashboardNavbar } from './DashboardNavbar';
import { AccordionSection } from './AccordionSection';
import { EmployeesSection } from './sections/EmployeesSection';
import { useTutorialContext } from '../../../tutorial/TutorialProvider';
import { ResearchProgressSection } from './sections/ResearchProgressSection';
import { MarketingSection } from './sections/MarketingSection';
import { EventLogSection } from './sections/EventLogSection';
import { ProductPortfolioSection } from './sections/ProductPortfolioSection';
import { TaskListSection } from './sections/TaskListSection';
import { MarketShareSection } from './sections/MarketShareSection';
import { FinanceSection } from './sections/FinanceSection';

// Import original tab components
import { ProductsView } from '../ProductsView';
import { FinanceView } from '../FinanceView';
import { ResearchViewStatic } from '../ResearchViewStatic';
import { MarketView } from '../MarketView';
import { EnhancedEventLog } from '../events/EnhancedEventLog';
import { OperationsView } from '../OperationsView';
import { CompanyView } from '../company/CompanyView';
import { useDynamicEvents } from '../../../stores/eventStore';
import { eventEngine } from '../../../services/tycoon/eventEngine';

import type { CompanyState } from '../../../types/company';
import type { Product, DesignConfig, ReviewSet } from '@smartphone-tycoon/shared-core';
import type { ResearchProject } from '../../../types/research';
import type { CapacityCheckResult } from '../../../types/buildings';

interface GameState {
  companyState: CompanyState;
  gameControls: {
    isPaused: boolean;
    gameSpeed: number;
  };
  events: unknown[];
  activeEvents?: unknown[];
  handleEventAction: (eventId: string, action: string) => void;
  onResearchStateChange?: (research: ResearchProject) => void;
  updateCompanyState?: (updates: Partial<CompanyState>) => void;
  fireWarningEnabled?: boolean;
  onHire?: (role: string, count: number) => void;
  onFire?: (role: string, count: number) => void;
  onEmployeeUpdate?: (employees: import('@/types/tycoon').Employee[]) => void;
  onHiringUpdate?: (processes: import('@/types/tycoon').HiringProcess[]) => void;
  onToggleFireWarning?: (show: boolean) => void;
  // Buildings
  buildings?: import('@/types/buildings').OwnedBuilding[];
  onBuildingsUpdate?: (buildings: import('@/types/buildings').OwnedBuilding[]) => void;
  buildingManagement?: {
    data: {
      buildings: import('@/types/buildings').OwnedBuilding[];
      availableBuildings: import('@/types/buildings').BuildingType[];
      capacity: import('@/types/buildings').BuildingCapacity;
      maintenanceCosts: import('@/types/buildings').BuildingCosts;
      capacityWarning: 'none' | 'low' | 'critical';
      dailyMaintenanceCost: number;
    };
    actions: {
      purchaseBuilding: (buildingTypeId: string) => boolean;
      sellBuilding: (buildingId: string) => boolean;
      checkCapacityForHiring: (role: string, count: number) => CapacityCheckResult;
      setBuildings: (buildings: import('@/types/buildings').OwnedBuilding[]) => void;
    };
  };
}

interface ProductPipeline {
  products: Product[];
  actions: {
    startDevelopment: (designConfig: DesignConfig, cash: number, updateCash: (newCash: number) => void) => Promise<{ success: boolean; error?: string }>;
    startProduction: (productId: string, gameDate: { day: number; month: number; year: number }) => Promise<{ success: boolean; error?: string }>;
    discontinueProduct: (productId: string) => Promise<{ success: boolean; error?: string }>;
    updateProductSales: (productSales: Array<{productId: string, unitsSold: number, revenue: number}>) => void;
    setProducts: (products: Product[]) => void;
  };
  utils?: {
    getPhaseProgress: (product: Product) => { progress: number; daysRemaining: number; totalDays: number };
    isPhaseComplete: (product: Product) => boolean;
    getProductAllocations?: () => Array<{
      productId: string;
      productName: string;
      allocations: Array<{
        phase: string;
        employeeType: string;
        count: number;
      }>;
      totalEmployees: number;
    }>;
    isProductStagePaused?: (productId: string) => boolean;
  };
}

interface GameSpeed {
  label: string;
  multiplier: number;
}

interface GameDashboardProps {
  gameState: GameState;
  productPipeline: ProductPipeline;
  onBack: () => void;
  companyName?: string;
  ceoName?: string;
  onProductAction?: (productId: string, action: string) => void;
  onTogglePause?: () => void;
  onChangeSpeed?: (speed: GameSpeed) => void;
  availableSpeeds?: GameSpeed[];
  releaseModalOpen?: boolean;
  productToRelease?: Product | null;
  productReviews?: ReviewSet | null;
  onReleaseModalChange?: (open: boolean, product: Product | null, reviews: ReviewSet | null) => void;
  discontinueModalOpen?: boolean;
  productToDiscontinue?: Product | null;
  onDiscontinueModalChange?: (open: boolean, product: Product | null) => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

// Removed unused ResearchViewComponentProps interface

export function GameDashboard({ gameState, productPipeline, onBack, companyName, ceoName, onProductAction, onTogglePause, onChangeSpeed, availableSpeeds, releaseModalOpen, productToRelease, productReviews, onReleaseModalChange, discontinueModalOpen, productToDiscontinue, onDiscontinueModalChange, activeTab: activeTabProp, onTabChange }: GameDashboardProps) {
  const [localActiveTab, setLocalActiveTab] = useState('Headquarters');
  const activeTab = activeTabProp || localActiveTab;
  const setActiveTab = onTabChange || setLocalActiveTab;
  const [showDevModeReference, setShowDevModeReference] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<string>('overview');
  const devModeEnabled = useDevModeStore(state => state.settings.enabled);
  const toggleDevMode = useDevModeStore(state => state.toggleDevMode);
  const { isTablet } = useDeviceType();

  // Get tutorial context
  const { isRunning: isTutorialRunning } = useTutorialContext();

  // Get dynamic events data for the Events tab
  const { data: eventsData, actions: eventsActions } = useDynamicEvents();
  
  // Get all products (player + competitor) for market share calculations
  const { allProducts } = useAllProducts();
  
  // Memoize the default onProductAction to prevent re-renders
  const defaultOnProductAction = useCallback(() => {}, []);
  const memoizedOnProductAction = useMemo(
    () => onProductAction || defaultOnProductAction,
    [onProductAction, defaultOnProductAction]
  );
  
  // Memoize gameDate to prevent object recreation
  const gameDate = useMemo(() => ({
    day: gameState.companyState.day,
    month: gameState.companyState.month,
    year: gameState.companyState.year
  }), [gameState.companyState.day, gameState.companyState.month, gameState.companyState.year]);
  
  // Navigation functions for section titles
  const navigateToTab = (tab: string) => {
    setActiveTab(tab);
  };
  
  const navigateToMarketProductListings = () => {
    setActiveTab('Marketing');
    // Note: The Marketing tab defaults to 'overview' view, but Product Listings is available as a subtab
  };
  
  const navigateToMarketMarketing = () => {
    setActiveTab('Marketing');
    // Note: Marketing will be the second subtab in Marketing
  };
  
  const navigateToBusinessStrategy = () => {
    setActiveTab('Operations');
    // Note: Strategy is a subtab under Operations
  };

  const toggleAccordion = (section: string) => {
    setActiveAccordion(activeAccordion === section ? '' : section);
  };

  const renderContent = () => {
    if (activeTab === 'Headquarters') {
      // Tablet: Accordion layout
      if (isTablet) {
        return (
          <div className="h-full overflow-y-auto p-4">
            <AccordionSection
              title="Overview"
              isOpen={activeAccordion === 'overview'}
              onToggle={() => toggleAccordion('overview')}
            >
              <EventLogSection
                events={gameState.events}
                onEventAction={gameState.handleEventAction}
                activeEvents={gameState.activeEvents || []}
                onNavigate={() => navigateToTab('Calendar')}
              />
              <ProductPortfolioSection
                products={productPipeline.products}
                onProductAction={memoizedOnProductAction}
                onNavigate={() => navigateToTab('Products')}
                companyName={companyName}
              />
            </AccordionSection>

            <AccordionSection
              title="Operations"
              isOpen={activeAccordion === 'operations'}
              onToggle={() => toggleAccordion('operations')}
            >
              <EmployeesSection
                companyState={gameState.companyState}
                onNavigate={() => navigateToTab('Operations')}
              />
              <TaskListSection
                companyState={gameState.companyState}
                products={productPipeline.products}
                onNavigate={navigateToBusinessStrategy}
              />
            </AccordionSection>

            <AccordionSection
              title="Finance"
              isOpen={activeAccordion === 'finance'}
              onToggle={() => toggleAccordion('finance')}
            >
              <FinanceSection
                companyState={gameState.companyState}
                onNavigate={() => navigateToTab('Finance')}
              />
              <MarketShareSection
                companyState={gameState.companyState}
                products={productPipeline.products}
                companyName={companyName}
                onNavigate={navigateToMarketProductListings}
                showTopSelling={true}
              />
            </AccordionSection>

            <AccordionSection
              title="Marketing & Research"
              isOpen={activeAccordion === 'marketing'}
              onToggle={() => toggleAccordion('marketing')}
            >
              <MarketingSection
                companyState={gameState.companyState}
                onNavigate={navigateToMarketMarketing}
              />
              <ResearchProgressSection
                companyState={gameState.companyState}
                onNavigate={() => navigateToTab('Research')}
              />
            </AccordionSection>
          </div>
        );
      }

      // Desktop: Original 3-column layout for Office tab
      return (
        <div className="grid grid-cols-[300px_1fr_300px] gap-4 h-full overflow-hidden">
          {/* Left Column */}
          <div className="grid grid-rows-[1fr_1fr_1fr] gap-4 h-full min-h-0">
            <EmployeesSection
              companyState={gameState.companyState}
              onNavigate={() => navigateToTab('Operations')}
            />
            <ResearchProgressSection
              companyState={gameState.companyState}
              onNavigate={() => navigateToTab('Research')}
            />
            <FinanceSection
              companyState={gameState.companyState}
              onNavigate={() => navigateToTab('Finance')}
            />
          </div>

          {/* Center Column */}
          <div className="grid grid-rows-[180px_1fr] gap-4 h-full min-h-0">
            <EventLogSection
              events={gameState.events}
              onEventAction={gameState.handleEventAction}
              activeEvents={gameState.activeEvents || []}
              onNavigate={() => navigateToTab('Calendar')}
            />
            <ProductPortfolioSection
              products={productPipeline.products}
              onProductAction={memoizedOnProductAction}
              onNavigate={() => navigateToTab('Products')}
              companyName={companyName}
            />
          </div>

          {/* Right Column */}
          <div className="grid grid-rows-[1fr_1fr_1fr] gap-4 h-full min-h-0">
            <TaskListSection
              companyState={gameState.companyState}
              products={productPipeline.products}
              onNavigate={navigateToBusinessStrategy}
            />
            <MarketShareSection
              companyState={gameState.companyState}
              products={productPipeline.products}
              companyName={companyName}
              onNavigate={navigateToMarketProductListings}
              showTopSelling={true}
            />
            <MarketingSection
              companyState={gameState.companyState}
              onNavigate={navigateToMarketMarketing}
            />
          </div>
        </div>
      );
    } else {
      // Single large container for other tabs
      return (
        <div className="h-full overflow-hidden p-6">
          <div className="h-full">
            {activeTab === 'Operations' && (
              <OperationsView 
                companyState={gameState.companyState}
                products={productPipeline.products}
                activeModifiers={eventEngine.getActiveModifiers()}
                employees={gameState.companyState.employees || []}
                hiringProcesses={gameState.companyState.hiringProcesses || []}
                showFireWarning={gameState.fireWarningEnabled !== false}
                onHire={gameState.onHire}
                onFire={gameState.onFire}
                onEmployeeUpdate={gameState.onEmployeeUpdate}
                onHiringUpdate={gameState.onHiringUpdate}
                onToggleFireWarning={gameState.onToggleFireWarning}
                buildings={gameState.buildings || []}
                onBuildingsUpdate={gameState.onBuildingsUpdate}
                onCashUpdate={(newCash) => gameState.updateCompanyState?.({ cash: newCash })}
                onEvent={(gameState as any).addEvent}
                employeeAllocations={gameState.companyState.employeeAllocations || []}
                onStateUpdate={gameState.updateCompanyState}
              />
            )}
            {activeTab === 'Products' && (
              <ProductsView
                products={productPipeline.products}
                onProductAction={memoizedOnProductAction}
                gameDate={gameDate}
                companyState={gameState.companyState}
                companyName={companyName || 'Your Company'}
                ceoName={ceoName}
                getPhaseProgress={productPipeline.utils?.getPhaseProgress}
                releaseModalOpen={releaseModalOpen}
                productToRelease={productToRelease}
                productReviews={productReviews}
                onReleaseModalChange={onReleaseModalChange}
                discontinueModalOpen={discontinueModalOpen}
                productToDiscontinue={productToDiscontinue}
                onDiscontinueModalChange={onDiscontinueModalChange}
                productAllocations={productPipeline.utils?.getProductAllocations?.()}
                isProductStagePaused={productPipeline.utils?.isProductStagePaused}
                onCashUpdate={(newCash) => gameState.updateCompanyState?.({ cash: newCash })}
                onEvent={(gameState as any).addEvent}
              />
            )}
            {activeTab === 'Finance' && (
              <div className="h-full" data-tutorial="financial-reports">
                <FinanceView
                  companyState={gameState.companyState}
                  products={productPipeline.products}
                />
              </div>
            )}
            {activeTab === 'Research' && (
              <div className="h-full" data-tutorial="research-tree">
                <ResearchViewStatic />
              </div>
            )}
            {activeTab === 'Marketing' && (
              <div className="h-full" data-tutorial="market-insights">
                {/* {console.log('[GameDashboard] Passing reputation to MarketView:', gameState.companyState.reputation)} */}
                <MarketView
                  companyState={gameState.companyState}
                  products={productPipeline.products}
                  companyName={companyName}
                  ceoName={ceoName}
                  activeModifiers={eventEngine.getActiveModifiers()}
                />
              </div>
            )}
            {activeTab === 'Company' && (
              <div className="h-full" data-tutorial="company-profile">
                <CompanyView
                  companyState={gameState.companyState}
                  products={productPipeline.products}
                  employees={gameState.companyState.employees || []}
                  hiringProcesses={gameState.companyState.hiringProcesses || []}
                />
              </div>
            )}
            {activeTab === 'Calendar' && (
              <div className="h-full" data-tutorial="events-log">
                <EnhancedEventLog
                  events={[...(eventsData.eventHistory || []), ...(eventsData.activeEvents || []), ...(eventsData.pendingReactiveEvents || [])]}
                  activeModifiers={eventEngine.getActiveModifiers()}
                  onViewDetails={(event) => {
                    console.log('View event details:', event);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-theme-bg-secondary">
      <div className="w-full h-full grid grid-rows-[80px_1fr] gap-4 p-4 max-h-screen">
        {/* Navigation Bar */}
        {/* Debug logging for props being passed to DashboardNavbar */}
        {/* {console.log(`[${new Date().toISOString()}] [GameDashboard] Passing to DashboardNavbar:`, {
          cash: gameState?.companyState?.cash,
          dailyStatsProfit: gameState?.companyState?.dailyStats?.profit,
          companyStateRef: gameState?.companyState,
          gameStateRef: gameState
        })} */}
        <DashboardNavbar 
          companyState={gameState.companyState}
          gameControls={gameState.gameControls}
          onBack={onBack}
          companyName={companyName}
          onTogglePause={onTogglePause}
          onChangeSpeed={onChangeSpeed}
          availableSpeeds={availableSpeeds}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Main Content */}
        {renderContent()}
      </div>
      
      {/* Dev Mode button - bottom left corner */}
      {devModeEnabled && (
        <div className="fixed bottom-4 left-4">
          <button
            onClick={() => setShowDevModeReference(true)}
            className="flex items-center gap-2 px-3 py-2 bg-theme-bg-content/10 hover:bg-theme-bg-content/20 rounded-lg transition-colors text-theme-text-secondary border border-theme-border backdrop-blur-sm shadow-lg"
          >
            <Code2 className="w-4 h-4" />
            <span className="text-sm">Dev Mode</span>
          </button>
        </div>
      )}
      
      {/* Dev Mode Reference Modal */}
      <DevModeReference
        isOpen={showDevModeReference}
        onClose={() => setShowDevModeReference(false)}
      />
    </div>
  );
}